import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, Btn, Card } from "@/components/common";
import Kpis from "@/components/previsionale/Kpis";
import { CostSettings, TargetSettings } from "@/components/previsionale/Settings";
import { CumulativeChart, MonthlyBarsChart, OccupancyChart, PickupChart, ChartLegend } from "@/components/previsionale/Charts";
import Assumptions, { Warnings } from "@/components/previsionale/Assumptions";
import MonthParams from "@/components/previsionale/MonthParams";
import TargetQuick from "@/components/previsionale/TargetQuick";
import MonthTable from "@/components/previsionale/MonthTable";
import Scenarios from "@/components/previsionale/Scenarios";
import SpeseDaConfermare from "@/components/previsionale/SpeseDaConfermare";
import { exportPrevisionalePDF } from "@/lib/previsionalePdf";
import { FileDown } from "lucide-react";

export default function Previsionale() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [scenario, setScenario] = useState("base");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const chartsRef = useRef(null);

  const load = useCallback(() => api.get(`/previsionale?year=${year}&scenario=${scenario}`).then((r) => setD(r.data)).catch(() => toast.error("Caricamento non riuscito")), [year, scenario]);
  useEffect(() => { setD(null); load(); }, [load]);

  const exportPdf = async () => {
    setBusy(true);
    try { await exportPrevisionalePDF(d, chartsRef.current); } catch (e) { toast.error("Esportazione non riuscita"); }
    setBusy(false);
  };

  return (
    <div>
      <PageHeader title="Previsionale" subtitle={`Consuntivo, previsione e obiettivo · anno ${year}`}>
        <div className="flex flex-wrap items-center gap-2 max-w-full">
        <div className="flex rounded-md border border-slate-200 overflow-hidden" data-testid="pv-scenario-selector">
          {[["prudente", "Prudente"], ["base", "Base"], ["ottimistico", "Ottimistico"]].map(([k, l]) => (
            <button key={k} data-testid={`pv-scenario-btn-${k}`} onClick={() => setScenario(k)}
              className={`px-3 py-2 text-sm font-medium transition-colors ${k === scenario ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
        <div className="flex rounded-md border border-slate-200 overflow-hidden" data-testid="pv-year-selector">
          {(d?.years || [year - 1, year, year + 1]).map((y) => (
            <button key={y} data-testid={`pv-year-${y}`} onClick={() => setYear(y)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${y === year ? "bg-sky-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{y}</button>
          ))}
        </div>
        <Btn variant="secondary" data-testid="pv-export-pdf" onClick={exportPdf} disabled={!d || busy}><FileDown size={16} /> {busy ? "Esportazione…" : "Esporta PDF"}</Btn>
        </div>
      </PageHeader>
      {!d ? <Card className="p-8 text-center text-slate-400">Caricamento previsionale…</Card> : (
        <div className="space-y-4">
          <TargetQuick year={year} target={d.target} onSaved={load} />
          <Kpis d={d} />
          <Scenarios d={d} />
          <Warnings v={d.v2} />
          <Assumptions d={d} />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" ref={chartsRef} data-testid="pv-charts">
            <ChartLegend />
            <CumulativeChart d={d} />
            <MonthlyBarsChart d={d} />
            <OccupancyChart d={d} />
            <PickupChart d={d} />
          </div>
          <MonthTable d={d} onSaved={load} />
          <div id="spese-da-confermare"><SpeseDaConfermare onChanged={load} /></div>
          <MonthParams d={d} onSaved={load} />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <TargetSettings year={year} target={d.target} onSaved={load} />
            <CostSettings costs={d.costs} onSaved={load} />
          </div>
        </div>
      )}
    </div>
  );
}
